/**
 * FILE: PollViewModal.js
 * PURPOSE: The "Closer Look" window for a poll.
 * WHAT IT DOES: It lets you see a single poll in full screen. If you made the poll, you can toggle between the "Poll" view and the "Results" view.
 * LAYMAN SUMMARY: Use this when you click on a poll from your history or saved list to see the details.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Bookmark, Share2, MoreVertical } from 'lucide-react';
import PollCard from './PollCard';
import { useModalA11y } from '../hooks/useModalA11y';

export default function PollViewModal({ isOpen, onClose, poll, initialSelection = null, onVote, votedPolls, darkMode, anonymousId, onNext, onPrev, hasPrev, hasNext, pollExposure, registerVisibility, onViewInsights, isSaved, onBookmarkToggle }) {
    const [activeTab, setActiveTab] = React.useState('poll');
    const containerRef = useModalA11y(isOpen, onClose);

    React.useEffect(() => {
        if (poll && votedPolls[poll.id] === 'revealed') {
            setActiveTab('results');
        } else {
            setActiveTab('poll');
        }
    }, [poll?.id, votedPolls]);

    if (!isOpen || !poll) return null;

    const isCreator = anonymousId && poll.creator_id && anonymousId === poll.creator_id;

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
                initial={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                animate={{ opacity: 1, backdropFilter: 'blur(12px)' }}
                exit={{ opacity: 0, backdropFilter: 'blur(0px)' }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 bg-black/60 pointer-events-auto"
                onClick={onClose}
            />

            {/* Relative Wrapper for Modal + Navigation */}
            <div ref={containerRef} role="dialog" aria-modal="true" aria-label={poll.question} className="relative w-full max-w-[588px] flex items-center justify-center pointer-events-none">

                {/* Navigation Arrows — desktop version sits outside the card bounds */}
                <div className={`absolute inset-y-0 -left-12 xl:-left-20 hidden md:flex items-center z-[300] ${!hasPrev ? 'pointer-events-none' : 'pointer-events-auto'}`}>
                    <button
                        onClick={onPrev}
                        disabled={!hasPrev}
                        aria-label="Previous poll"
                        className={`p-5 rounded-full backdrop-blur-xl border-2 transition-all shadow-2xl ${!hasPrev
                            ? 'opacity-0'
                            : 'bg-primary-600/20 border-primary-600/30 text-white hover:bg-primary-600 hover:border-primary-600 hover:scale-110 active:scale-95'
                            }`}
                    >
                        <ChevronLeft size={44} strokeWidth={2.5} />
                    </button>
                </div>

                <div className={`absolute inset-y-0 -right-12 xl:-right-20 hidden md:flex items-center z-[300] ${!hasNext ? 'pointer-events-none' : 'pointer-events-auto'}`}>
                    <button
                        onClick={onNext}
                        disabled={!hasNext}
                        aria-label="Next poll"
                        className={`p-5 rounded-full backdrop-blur-xl border-2 transition-all shadow-2xl ${!hasNext
                            ? 'opacity-0'
                            : 'bg-primary-600/20 border-primary-600/30 text-white hover:bg-primary-600 hover:border-primary-600 hover:scale-110 active:scale-95'
                            }`}
                    >
                        <ChevronRight size={44} strokeWidth={2.5} />
                    </button>
                </div>

                {/* Mobile equivalent — fixed to the viewport edges instead of the card bounds,
                    since on narrow screens the card has almost no margin to place arrows outside it. */}
                {hasPrev && (
                    <button
                        onClick={onPrev}
                        aria-label="Previous poll"
                        className="md:hidden fixed left-2 top-1/2 -translate-y-1/2 z-[300] p-2.5 rounded-full bg-black/40 backdrop-blur-xl border border-white/20 text-white pointer-events-auto active:scale-90 transition-all"
                    >
                        <ChevronLeft size={22} strokeWidth={2.5} />
                    </button>
                )}
                {hasNext && (
                    <button
                        onClick={onNext}
                        aria-label="Next poll"
                        className="md:hidden fixed right-2 top-1/2 -translate-y-1/2 z-[300] p-2.5 rounded-full bg-black/40 backdrop-blur-xl border border-white/20 text-white pointer-events-auto active:scale-90 transition-all"
                    >
                        <ChevronRight size={22} strokeWidth={2.5} />
                    </button>
                )}

                {/* Modal Container */}
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: "easeOut" }}
                    className={`relative w-full max-h-[96vh] rounded-[32px] shadow-[0_32px_120px_rgba(0,0,0,0.8)] border overflow-hidden flex flex-col pointer-events-auto z-[205] ${darkMode ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'
                    }`}
                >
                    {/* Header / Tab Switcher */}
                    <div className="absolute top-6 left-6 z-[100] flex items-center justify-between pointer-events-none w-[calc(100%-48px)]">
                        {isCreator && (
                            <div className="flex bg-gray-100/10 backdrop-blur-md p-1.5 rounded-2xl border border-white/10 pointer-events-auto">
                                <button
                                    onClick={() => setActiveTab('poll')}
                                    className={`px-6 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'poll'
                                        ? 'bg-white dark:bg-gray-700 text-primary-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-600 dark:hover:text-gray-200'
                                        }`}
                                >
                                    Poll
                                </button>
                                <button
                                    onClick={() => setActiveTab('results')}
                                    className={`px-6 py-2 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeTab === 'results'
                                        ? 'bg-white dark:bg-gray-700 text-primary-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-600 dark:hover:text-gray-200'
                                        }`}
                                >
                                    Results
                                </button>
                            </div>
                        )}

                        <button
                            onClick={onClose}
                            aria-label="Close poll"
                            className="ml-auto p-3 bg-black/20 backdrop-blur-md hover:bg-black/40 rounded-full transition-all text-white border border-white/10 hover:scale-110 pointer-events-auto"
                        >
                            <X size={24} />
                        </button>
                    </div>

                    {/* Poll Content */}
                    <div className="flex-1 overflow-y-auto no-scrollbar h-full">
                        <PollCard
                            key={poll.id}
                            id={poll.id}
                            voted={activeTab === 'results' || votedPolls[poll.id] === 'revealed'}
                            onVote={onVote}
                            onViewInsights={() => { if (onViewInsights) onViewInsights(poll); }}
                            question={poll.question}
                            category={poll.category || "General"}
                            isPrivate={poll.is_trap}
                            type={poll.poll_type || "single"}
                            votesToPublic={25}
                            img={poll.background_url}
                            options={poll.options}
                            postVoteInsight={poll.postVoteInsight}
                            totalVoters={poll.total_voters || 0}
                            expires_at={poll.expires_at}
                            anonymousId={anonymousId}
                            creator_id={poll.creator_id}
                            inModal={true}
                            pollExposure={pollExposure?.[poll.id] || 0}
                            registerVisibility={registerVisibility}
                            votedStatus={votedPolls[poll.id]}
                            initialSelection={initialSelection}
                            isSaved={isSaved}
                            onBookmarkToggle={onBookmarkToggle}
                        />
                    </div>

                </motion.div>
            </div>
        </div>
    );
}
